import { useMemo } from 'react'

import cn from 'classnames'
import { Loader2 } from 'lucide-react'

import { ChainId } from '@src/configs/chainInfo'
import { ResultCode, StepNames } from '@src/enums'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { IExchangeData } from '@src/types/exchange'
import { IQuoteResponse } from '@src/types/order'
import { isFeatureEnabled } from '@src/utils'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { useApplicationStore } from '@src/zustand_stores/application.store'

interface IErrors {
    isInsufficientBalance?: boolean
    isQuoteInvalid?: boolean
    isBuyOutputLoading?: boolean
    isDestinationSolana?: boolean
    isOriginSolana?: boolean
    hasInsufficientGas?: boolean
    isTransactionCapped?: boolean
}

interface Props extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    errorsJSON: IErrors
    sellData: IExchangeData
    buyData?: IExchangeData
    sellAmountString?: string
    buttonText?: string
    isCustomWallet?: boolean
    destinationAddress?: string
    liquiditySource?: string
    isOrderRejected?: boolean
    isQuoteExpired?: boolean
    currentStep?: StepNames
    hasInsufficientGas?: boolean
    quoteResponse?: IQuoteResponse
    gasFeeSimulationInProgress?: boolean
    isQuoteLoading?: boolean
    isQuoteFetching?: boolean
    disabled?: boolean
    onMetaMaskPopup?: (step: StepNames) => void
}

export const SwapButton = ({
    errorsJSON,
    sellData,
    buyData,
    className,
    buttonText = 'Swap',
    isCustomWallet = false,
    destinationAddress = '',
    liquiditySource,
    isOrderRejected = false,
    isQuoteExpired = false,
    currentStep = StepNames.UNSTARTED,
    hasInsufficientGas = false,
    quoteResponse = undefined,
    onMetaMaskPopup,
    gasFeeSimulationInProgress,
    sellAmountString = undefined,
    disabled,
    ...props
}: Props) => {
    const { waitingForConnectedWalletInteraction } = useApplicationStore()
    const { isSolanaConnected, isTronConnected, isEVMConnected, EVMChainId } = useWalletConnections()

    const isBuyChainWalletConnected = useMemo(() => {
        if (!buyData) {
            return true // Skip check if buyData isn't available
        }

        switch (buyData.chainId) {
            case ChainId.Solana:
                return isSolanaConnected
            case ChainId.Tron:
                return isTronConnected
            default:
                return isEVMConnected
        }
    }, [buyData, isSolanaConnected, isTronConnected, isEVMConnected, EVMChainId])

    const getButtonText = () => {
        if (waitingForConnectedWalletInteraction) {
            return 'Processing...'
        }

        // Check for input first, before other conditions
        if (inputRequired || sellAmountString === '0') {
            return 'Enter an amount'
        }

        if (isInsufficientBalance) {
            return `Insufficient ${sellData?.token?.symbol ?? ''} balance`
        }

        if (hasInsufficientGas) {
            return `Insufficient gas for fees`
        }

        if (buyData && isWrapOnly(sellData, buyData)) {
            if (gasFeeSimulationInProgress) {
                return 'Simulating gas costs...'
            } else {
                return `Wrap ${sellData.token.symbol}`
            }
        } else if (buyData && isUnWrapOnly(sellData, buyData)) {
            return `Unwrap ${sellData.token.symbol}`
        } else {
            if (isCustomWallet && destinationAddress === '') {
                return 'Add Recipient'
            }
            if (isBuyOutputLoading) {
                return 'Fetching quotes...'
            }
            if (!isBuyOutputLoading && quoteResponse && quoteResponse.status === ResultCode.failure) {
                return 'Unable to get quote - Please try again'
            }
            if (isOrderRejected) {
                return 'Order too small. Please increase your trade size'
            }
            if (isFeatureEnabled('USE_TESTNETS_ONLY') && (isQuoteInvalid || isLiquiditySourceUnavailable)) {
                return 'Unable to fill order amount at this time'
            }
            if (isQuoteExpired) {
                return 'Quote Expired - Please Try Again'
            }
        }

        if (gasFeeSimulationInProgress) {
            return 'Simulating gas costs...'
        }

        // Add this check before other conditions
        if (!isCustomWallet && !isBuyChainWalletConnected) {
            return 'Confirm Destination Address'
        }

        // Create a mapping of steps to their button text
        const stepToText: { [key in StepNames]?: string } = {
            [StepNames.SWITCH_NETWORKS]: 'Switch Network',
            [StepNames.DEPOSIT_WETH]: `Wrap ${sellData.token?.symbol || 'Token'}`,
            [StepNames.DEPOSIT_WETH_PENDING]: 'Wrapping...',
            [StepNames.APPROVE_TOKENS]: `Approve ${sellData.token?.symbol || 'Token'}`,
            [StepNames.APPROVE_TOKENS_PENDING]: 'Approving...',
            [StepNames.CONFIRM_SWAP]: 'Confirm Swap',
            [StepNames.CONFIRM_SWAP_PENDING]: 'Confirming Swap',
            [StepNames.MATCHING]: 'Matching Order',
            [StepNames.MATCH_CREATED]: 'Match Created',
            [StepNames.MATCH_EXECUTED]: 'Match Executed',
        }

        // Get text from mapping or use default
        return stepToText[currentStep] || buttonText || 'Confirm Swap'
    }

    const isPendingState = [
        StepNames.DEPOSIT_WETH_PENDING,
        StepNames.APPROVE_TOKENS_PENDING,
        StepNames.CONFIRM_SWAP_PENDING,
        StepNames.MATCHING,
        StepNames.MATCH_CREATED,
        StepNames.MATCH_EXECUTED,
    ].includes(currentStep)

    const { isInsufficientBalance = false, isQuoteInvalid = false, isBuyOutputLoading = false } = errorsJSON

    const inputRequired = Number(sellData?.value) === 0 && Number(buyData?.value) === 0

    const isLiquiditySourceUnavailable = !inputRequired && liquiditySource === 'unavailable'

    const hasQuoteErrors =
        isQuoteInvalid ||
        isBuyOutputLoading ||
        isLiquiditySourceUnavailable ||
        props.isQuoteLoading === true ||
        props.isQuoteFetching === true ||
        (!isBuyOutputLoading && quoteResponse && quoteResponse.status === ResultCode.failure)

    const hasNonQuoteErrors = isInsufficientBalance || inputRequired

    let hasAnyErrors = hasNonQuoteErrors

    // Only consider quote-related errors if we rely on quote.
    // Wrap/unwrap does not need this.
    if (buyData && !(isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData))) {
        hasAnyErrors = hasAnyErrors || hasQuoteErrors === true
    }

    const buttonDisabled =
        disabled ||
        hasAnyErrors ||
        waitingForConnectedWalletInteraction ||
        isOrderRejected ||
        hasInsufficientGas ||
        (isCustomWallet && destinationAddress === '') ||
        isPendingState ||
        sellData.value === BigInt(0) ||
        gasFeeSimulationInProgress ||
        sellAmountString === '0' ||
        (!isCustomWallet && !isBuyChainWalletConnected)

    const buttonClass = cn(
        'flex select-none items-center justify-center space-x-[10px] rounded-2xl px-[18px] py-[14px] font-medium leading-7 transition-all',
        {
            'bg-mach-component-background': buttonDisabled, // Should use a single variable to indicate disabled button
            'bg-mach-button-color hover:brightness-110': !buttonDisabled, // Should use a single variable to indicate disabled button
        },
        className
    )

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (currentStep === StepNames.DEPOSIT_WETH) {
            onMetaMaskPopup && onMetaMaskPopup(StepNames.DEPOSIT_WETH_PENDING)
        } else if (currentStep === StepNames.APPROVE_TOKENS) {
            onMetaMaskPopup && onMetaMaskPopup(StepNames.APPROVE_TOKENS_PENDING)
        } else if (currentStep === StepNames.CONFIRM_SWAP) {
            onMetaMaskPopup && onMetaMaskPopup(StepNames.CONFIRM_SWAP_PENDING)
        }
        // Call the original onClick handler with the event
        props.onClick && props.onClick(event)
    }

    return (
        <button className={buttonClass} disabled={buttonDisabled} onClick={handleButtonClick} {...props}>
            <div className='flex items-center justify-center gap-2'>
                {isPendingState && <Loader2 className='h-5 w-5 animate-spin' />}
                {getButtonText()}
            </div>
        </button>
    )
}
